import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../app/store';
import { selectCompaniesAreFetching } from '../../../../../features/company/companySlice';
import { selectCompaniesGroups } from '../../../../../features/groups/groupSlice';
import { PIECHART_COLORS } from '../../../../../features/reports/common';
import { selectCompaniesContents, selectCompaniesContentsAreFetching, selectGroupsContents } from '../../../../../features/reports/learning/common/companiesContent';
import { IFulfillmentPerCompany, LearningType } from '../../../../../features/reports/learning/common/interfaces';
import { selectLessonsFulfillments, selectLessonsFulfillmentsFetching } from '../../../../../features/reports/learning/lessons/fulfillment';
import { computeGroupsFulfillment } from '../../common/computeGroupsFulfillment';
import { selectGroupStudent } from '../../../../../features/users/usersSlice';
import LoadingOverlay from '../../../../common/LoadingOverlay';
import { RoundedContainer } from '../../../../common/RoundedContainer';
import { DisplayDirection, IconNames } from '../../../common/SimpleStat';
import { StatData, StatsRow } from '../../../common/StatsRow';
import { getTranslations } from '../../../../../features/translations/translationsUtils';

interface LessonsIndicatorsProps {
  companyId?: string,
  selectFulfillmentsPerCompany: (state: RootState) => IFulfillmentPerCompany;
  selectFulfillmentsPerCompanyFetching: (state: RootState) => boolean;
}

export const LessonsIndicators: FunctionComponent<LessonsIndicatorsProps> = (
  {
    selectFulfillmentsPerCompanyFetching,
    companyId
  }
) => {
  const [data, setData] = useState<[number, number]>([0, 0]);
  const companiesContent = useSelector(selectCompaniesContents);
  const contentsAreFetching = useSelector(selectCompaniesContentsAreFetching);
  // Companies
  const isCompaniesFetching = useSelector(selectCompaniesAreFetching);
  // Fulfillments
  const isFulfillmentsFetching = useSelector(selectFulfillmentsPerCompanyFetching);
  // Per-student fulfillments (fetched by FulfillmentChart for the whole
  // company, not narrowed by its group selector)
  const lessonsFulfillments = useSelector(selectLessonsFulfillments);
  const isLessonsFulfillmentsFetching = useSelector(selectLessonsFulfillmentsFetching);
  const groupIdPerStudent = useSelector(selectGroupStudent);
  //Groups
  const companiesGroups = useSelector(selectCompaniesGroups);
  const groupsContents = useSelector(selectGroupsContents);

  const fetching = isCompaniesFetching ||
    isFulfillmentsFetching ||
    isLessonsFulfillmentsFetching ||
    contentsAreFetching;

  useEffect(()=>{
    if(fetching || Object.keys(companiesContent).length === 0){
      return;
    }
    const companiesIds = companyId ? [companyId] : Object.keys(companiesGroups);
    const groupsIds: string[] = [];
    companiesIds.forEach(id => groupsIds.push(...(companiesGroups[id] || [])));
    const { completed, total } = computeGroupsFulfillment({
      fulfillments: lessonsFulfillments,
      groupIdPerStudent,
      groupsContents,
      groupsIds,
      learningType: LearningType.lessons,
    });
    setData([completed, total - completed]);
  },[fetching, companiesContent, companyId, companiesGroups, groupsContents, lessonsFulfillments, groupIdPerStudent]);

  let count = 0;
  if(companyId){
    count = companiesContent[companyId]?(Array.from(new Set(companiesContent[companyId].lessons))).length : 0;
  }else{
    const lessonsIds:string[] = [];
    Object.values(companiesContent).forEach(companyContents => {
      lessonsIds.push(...companyContents.lessons);
    });
    count = (Array.from(new Set(lessonsIds))).length;
  }
  const [completed, rest] = data;
  let total = completed + rest;
  const percentage = Math.round(completed/(total ? total : 1) * 100);
  const statsData: StatData = [
    {
      iconName: IconNames.Availability,
      mainText: `${count}`,
      description: getTranslations('REPORT_AVAILABLE_LESSONS'),
      direction:DisplayDirection.row
    },
    {
      percentValue: percentage.toFixed(2),
      description: <div>{getTranslations('REPORT_PERCENT_FINISHED_LESSONS')}</div>,
      canvasHeight: 100,
      type: '2',
      data: {
        labels: [
          getTranslations('REPORT_LESSONS_FINISHED'),
          getTranslations('REPORT_LESSONS_UNFINISHED'),
        ],
        datasets: [{
          label: '',
          data,
          backgroundColor: [
            PIECHART_COLORS[0],
            PIECHART_COLORS[1],
          ],
          hoverOffset: 4
        }],
      },
    }
  ];
  return <RoundedContainer>
    {contentsAreFetching ? 
      <LoadingOverlay inline height={300} spinning /> :
      <StatsRow statsData={statsData} />
    }
  </RoundedContainer>;
}